import React, { useEffect, useState } from "react";
import "../../css/CategoryPage.css";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth";
import AdminHeader from "../../components/header/AdminHeader";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NewReleasesIcon from "@mui/icons-material/NewReleases";

const AdminOrders = () => {
  const [auth] = useAuth();
  const [orders, setOrders] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingOrderId, setProcessingOrderId] = useState(null);

  const apiUrl = `${process.env.REACT_APP_API}`;

  useEffect(() => {
    if (auth.token) getOrders();
  }, [auth.token]);

  const getOrders = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${apiUrl}/all-orders`);

      // Filter only new orders and sort by date (oldest first for processing)
      const newOrders = data
          .filter((order) => order.status === "Order Received")
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      setOrders(newOrders);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      toast.error("Error loading orders");
      setIsLoading(false);
    }
  };

  const handleTrackingNumberChange = (orderId, value) => {
    setTrackingNumbers({
      ...trackingNumbers,
      [orderId]: value,
    });
  };

  const handleShipOrder = async (orderId) => {
    const trackingNumber = trackingNumbers[orderId];

    if (!trackingNumber || trackingNumber.trim() === "") {
      toast.error("Please enter a tracking number before shipping");
      return;
    }

    try {
      setProcessingOrderId(orderId);
      await axios.put(`${apiUrl}/order-status/${orderId}`, {
        status: "Shipped",
        trackingNumber: trackingNumber,
      });

      toast.success("Order marked as Shipped! Customer will be notified.");
      getOrders();

      // Clear the tracking number from state
      const newTrackingNumbers = { ...trackingNumbers };
      delete newTrackingNumbers[orderId];
      setTrackingNumbers(newTrackingNumbers);
      setProcessingOrderId(null);
    } catch (err) {
      console.log(err);
      toast.error("Error shipping order");
      setProcessingOrderId(null);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      setProcessingOrderId(orderId);
      await axios.put(`${apiUrl}/order-status/${orderId}`, {
        status: "Cancelled",
        trackingNumber: "",
      });

      toast.success("Order cancelled. Customer will be notified.");
      getOrders();
      setShowCancelConfirm(null);
      setProcessingOrderId(null);
    } catch (err) {
      console.log(err);
      toast.error("Error cancelling order");
      setProcessingOrderId(null);
    }
  };

  return (
      <>
        <AdminHeader />
        <div className="container my-5">
          <div className="d-flex align-items-center justify-content-center mb-4">
            <NewReleasesIcon style={{ fontSize: "2.5rem", color: "#002E6A", marginRight: "10px" }} />
            <h1 style={{ color: "#002E6A", fontWeight: "600", margin: 0 }}>
              New Orders
            </h1>
          </div>

          {/* Summary Card */}
          <div className="alert alert-info text-center mb-4">
            <strong>{orders.length}</strong> {orders.length === 1 ? "order" : "orders"} waiting to be processed
          </div>

          {/* Loading State */}
          {isLoading && (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading orders...</p>
              </div>
          )}

          {/* No Orders */}
          {!isLoading && orders.length === 0 && (
              <div className="text-center my-5">
                <div className="card shadow-sm p-5">
                  <h4>🎉 All caught up!</h4>
                  <p className="text-muted">There are no new orders to process right now.</p>
                </div>
              </div>
          )}

          {/* Orders List */}
          {!isLoading && orders.length > 0 && (
              <>
                {orders.map((order, index) => (
                    <div key={order._id} className="card shadow-sm mb-4 order-card">
                      <div className="card-header bg-warning text-dark">
                        <div className="row align-items-center">
                          <div className="col-md-6">
                            <h5 className="mb-0">
                              <NewReleasesIcon /> Order #{orders.length - index} - Needs Processing
                            </h5>
                          </div>
                          <div className="col-md-6 text-md-end">
                            <small>
                              Received: <strong>{new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</strong>
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="row">
                          {/* Customer Information */}
                          <div className="col-lg-6 mb-4">
                            <h6 className="border-bottom pb-2 mb-3 text-primary">
                              <EmailIcon fontSize="small" /> Customer Information
                              {order.isGuestOrder && <span className="badge bg-info ms-2">Guest</span>}
                            </h6>
                            <div className="customer-info">
                              {order.isGuestOrder ? (
                                <>
                                  <p className="mb-2">
                                    <strong>Name:</strong> {order.guestInfo.firstName} {order.guestInfo.lastName}
                                  </p>
                                  <p className="mb-2">
                                    <EmailIcon fontSize="small" className="text-muted" /> {order.guestInfo.email}
                                  </p>
                                  <p className="mb-2">
                                    <PhoneIcon fontSize="small" className="text-muted" /> {order.guestInfo.phone}
                                  </p>
                                  <p className="mb-2">
                                    <LocationOnIcon fontSize="small" className="text-muted" />
                                    <strong> Shipping Address:</strong>
                                  </p>
                                  <div className="ms-4 text-muted">
                                    {order.guestInfo.address.street1}
                                    {order.guestInfo.address.street2 && <>, {order.guestInfo.address.street2}</>}
                                    <br />
                                    {order.guestInfo.address.city}, {order.guestInfo.address.state} {order.guestInfo.address.zip}
                                    <br />
                                    USA
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p className="mb-2">
                                    <strong>Name:</strong> {order.buyer.name.first} {order.buyer.name.last}
                                  </p>
                                  <p className="mb-2">
                                    <EmailIcon fontSize="small" className="text-muted" /> {order.buyer.email}
                                  </p>
                                  <p className="mb-2">
                                    <PhoneIcon fontSize="small" className="text-muted" /> {order.buyer.phone}
                                  </p>
                                  <p className="mb-2">
                                    <LocationOnIcon fontSize="small" className="text-muted" />
                                    <strong> Shipping Address:</strong>
                                  </p>
                                  <div className="ms-4 text-muted">
                                    {order.buyer.address.street1}
                                    {order.buyer.address.street2 && <>, {order.buyer.address.street2}</>}
                                    <br />
                                    {order.buyer.address.city}, {order.buyer.address.state} {order.buyer.address.zip}
                                    <br />
                                    USA
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Order Summary */}
                          <div className="col-lg-6 mb-4">
                            <h6 className="border-bottom pb-2 mb-3 text-primary">Order Summary</h6>
                            <div className="order-summary">
                              <div className="d-flex justify-content-between mb-2">
                                <span>Total Items:</span>
                                <strong>{order.products.length}</strong>
                              </div>
                              <div className="d-flex justify-content-between mb-3">
                                <span>Total Amount:</span>
                                <h4 className="text-success mb-0">${order.total}</h4>
                              </div>
                            </div>

                            {/* Shipping Action Section */}
                            <div className="shipping-section mt-4 p-3 bg-light rounded">
                              <h6 className="mb-3">
                                <LocalShippingIcon /> Ship This Order
                              </h6>
                              <div className="mb-3">
                                <label className="form-label"><strong>USPS Tracking Number:</strong></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter tracking number"
                                    value={trackingNumbers[order._id] || ""}
                                    onChange={(e) => handleTrackingNumberChange(order._id, e.target.value)}
                                    disabled={processingOrderId === order._id}
                                />
                              </div>
                              <button
                                  className="btn btn-success w-100 mb-2"
                                  onClick={() => handleShipOrder(order._id)}
                                  disabled={processingOrderId === order._id || !trackingNumbers[order._id]}
                              >
                                {processingOrderId === order._id ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Processing...
                                    </>
                                ) : (
                                    <>
                                      <LocalShippingIcon fontSize="small" /> Mark as Shipped
                                    </>
                                )}
                              </button>

                              {/* Cancel Order Section */}
                              {showCancelConfirm === order._id ? (
                                  <div className="alert alert-danger mb-0">
                                    <p className="mb-2"><strong>Are you sure you want to cancel this order?</strong></p>
                                    <button
                                        className="btn btn-danger btn-sm me-2"
                                        onClick={() => handleCancelOrder(order._id)}
                                        disabled={processingOrderId === order._id}
                                    >
                                      Yes, Cancel Order
                                    </button>
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowCancelConfirm(null)}
                                        disabled={processingOrderId === order._id}
                                    >
                                      No, Keep Order
                                    </button>
                                  </div>
                              ) : (
                                  <button
                                      className="btn btn-outline-danger w-100"
                                      onClick={() => setShowCancelConfirm(order._id)}
                                      disabled={processingOrderId === order._id}
                                  >
                                    <CancelIcon fontSize="small" /> Cancel This Order
                                  </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Products Section */}
                        <div className="mt-3">
                          <h6 className="border-bottom pb-2 mb-3 text-primary">
                            Products to Ship ({order.products.length} {order.products.length === 1 ? 'item' : 'items'})
                          </h6>
                          <div className="row g-3">
                            {order.products.map((p) => (
                                p.product && (
                                    <div key={p._id} className="col-12">
                                      <div className="card product-card">
                                        <div className="card-body">
                                          <div className="row align-items-center">
                                            <div className="col-md-2 text-center mb-3 mb-md-0">
                                              <img
                                                  src={p.product.images.front}
                                                  alt={p.product.productName}
                                                  className="img-fluid rounded"
                                                  style={{
                                                    maxWidth: "120px",
                                                    maxHeight: "120px",
                                                    objectFit: "contain",
                                                  }}
                                                  loading="lazy"
                                              />
                                            </div>
                                            <div className="col-md-7">
                                              <h6 className="mb-2">{p.product.productName}</h6>
                                              <div className="product-details">
                                                <span className="badge bg-secondary me-2">Size: {p.selectedSize}</span>
                                                <span className="badge bg-info me-2">Qty: {p.quantity}</span>
                                              </div>
                                            </div>
                                            <div className="col-md-3 text-md-end mt-2 mt-md-0">
                                              <h5 className="mb-0 text-success">
                                                ${p.product.salePrice > 0 ? p.product.salePrice : p.product.productPrice}
                                              </h5>
                                              <small className="text-muted">per item</small>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                )
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                ))}
              </>
          )}
        </div>

        <style jsx>{`
        .order-card {
          transition: transform 0.2s, box-shadow 0.2s;
          border-left: 5px solid #ffc107;
        }
        
        .order-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }

        .product-card {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
        }

        .customer-info p {
          line-height: 1.8;
        }

        .shipping-section {
          border: 2px dashed #28a745;
        }

        .badge {
          font-size: 0.85rem;
          padding: 0.35em 0.65em;
        }
      `}</style>
      </>
  );
};

export default AdminOrders;